import React from 'react';

import { AddressForm } from '@admin/components/address/address_form';
import { Selector as RegionsSelector } from '@admin/components/regions/selector';
import { TZS } from '@admin/constants/time_zone';
import { FacilityOfferingOperatorEnum, FacilityUpdateInput } from '@admin/schema';
import { AddressSection } from '@admin/styled/address_section';
import { Button } from '@admin/styled/button';
import { Field } from '@admin/styled/field';
import { FieldHint } from '@admin/styled/field_hint';
import { FieldLabel } from '@admin/styled/field_label';
import { FormBody } from '@admin/styled/form_body';
import { FormCard } from '@admin/styled/form_card';
import { FormFooter } from '@admin/styled/form_footer';
import { FormRow } from '@admin/styled/form_row';
import { OptionRow } from '@admin/styled/option_row';
import { OptionStack } from '@admin/styled/option_stack';

import { FacilitySelect } from './facility_select';
import { FacilityTextInput } from './facility_text_input';
import { DEFAULT_OPERATOR, OPERATOR_VALUES, operatorLabel } from './operator_options';

export type FacilityFormServiceType = {
  id: string;
  name: string;
  slug: string;
};

export type FacilityFormValues = FacilityUpdateInput;

function offeringForServiceType(offerings: FacilityUpdateInput['facilityServiceOfferings'], serviceTypeId: string) {
  return offerings.find((offering) => offering.facilityServiceTypeID === serviceTypeId);
}

function toggleServiceOffering(
  offerings: FacilityUpdateInput['facilityServiceOfferings'],
  serviceTypeId: string,
): FacilityUpdateInput['facilityServiceOfferings'] {
  const existing = offeringForServiceType(offerings, serviceTypeId);
  if (existing) {
    return offerings.filter((offering) => offering.facilityServiceTypeID !== serviceTypeId);
  }

  return [...offerings, { facilityServiceTypeID: serviceTypeId, operator: DEFAULT_OPERATOR }];
}

function setOfferingOperator(
  offerings: FacilityUpdateInput['facilityServiceOfferings'],
  serviceTypeId: string,
  operator: FacilityOfferingOperatorEnum,
): FacilityUpdateInput['facilityServiceOfferings'] {
  return offerings.map((offering) =>
    offering.facilityServiceTypeID === serviceTypeId ? { ...offering, operator } : offering,
  );
}

export const FacilityForm: React.FC<{
  initialValues: FacilityFormValues;
  allServiceTypes: FacilityFormServiceType[];
  onSubmit(values: FacilityUpdateInput): void;
  onCancel(): void;
  saving?: boolean;
}> = ({ initialValues, allServiceTypes, onSubmit, onCancel, saving }) => {
  const [values, setValues] = React.useState(initialValues);

  const canSave =
    values.name.trim().length > 0 &&
    values.code.trim().length > 0 &&
    values.tz.trim().length > 0 &&
    values.address.street.trim().length > 0 &&
    values.address.city.trim().length > 0 &&
    values.address.state.trim().length > 0 &&
    values.address.zip.trim().length > 0 &&
    values.facilityServiceOfferings.every((offering) => offering.operator);

  return (
    <FormCard>
      <FormBody>
        <Field>
          <FieldLabel htmlFor="facility_name">Name</FieldLabel>
          <FacilityTextInput
            id="facility_name"
            type="text"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            autoFocus
          />
        </Field>

        <FormRow>
          <Field>
            <FieldLabel htmlFor="facility_code">Code</FieldLabel>
            <FacilityTextInput
              id="facility_code"
              type="text"
              value={values.code}
              onChange={(e) => setValues({ ...values, code: e.target.value })}
            />
            <FieldHint>Unique identifier for admin and integrations.</FieldHint>
          </Field>

          <Field>
            <FieldLabel htmlFor="facility_tz">Time zone</FieldLabel>
            <FacilitySelect
              id="facility_tz"
              options={TZS}
              value={values.tz}
              onChange={(option) => setValues({ ...values, tz: option || '' })}
              prompt=" - Select Time Zone -"
            />
          </Field>
        </FormRow>

        <Field>
          <FieldLabel>Regions</FieldLabel>
          <RegionsSelector
            selectedIDs={values.regionIDs}
            onChange={(regionIDs) => setValues({ ...values, regionIDs })}
          />
        </Field>

        <Field>
          <FieldLabel>Service Offerings</FieldLabel>
          <OptionStack>
            {allServiceTypes.map((serviceType) => {
              const offering = offeringForServiceType(values.facilityServiceOfferings, serviceType.id);
              const selected = offering != null;

              return (
                <OptionRow key={serviceType.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        setValues({
                          ...values,
                          facilityServiceOfferings: toggleServiceOffering(
                            values.facilityServiceOfferings,
                            serviceType.id,
                          ),
                        })
                      }
                    />
                    {serviceType.name}
                  </label>
                  {selected && (
                    <FacilitySelect
                      aria-label={`Operator for ${serviceType.name}`}
                      options={OPERATOR_VALUES}
                      name={operatorLabel}
                      value={offering.operator}
                      onChange={(option) => {
                        if (!option) {
                          return;
                        }
                        setValues({
                          ...values,
                          facilityServiceOfferings: setOfferingOperator(
                            values.facilityServiceOfferings,
                            serviceType.id,
                            option,
                          ),
                        });
                      }}
                    />
                  )}
                </OptionRow>
              );
            })}
          </OptionStack>
        </Field>

        <Field>
          <FieldLabel>Address</FieldLabel>
          <AddressSection>
            <AddressForm address={values.address} onChange={(address) => setValues({ ...values, address })} />
          </AddressSection>
        </Field>
      </FormBody>
      <FormFooter>
        <Button variant="secondary" as="button" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={!canSave || saving}
          onClick={() =>
            onSubmit({
              name: values.name.trim(),
              code: values.code.trim(),
              active: values.active,
              tz: values.tz,
              regionIDs: values.regionIDs,
              facilityServiceOfferings: values.facilityServiceOfferings,
              address: {
                ...values.address,
                aptsuite: values.address.aptsuite || '',
                country: values.address.country || 'United States',
              },
            })
          }
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </FormFooter>
    </FormCard>
  );
};
